import { useContext } from 'react'

import Link from '@atoms/Link'
import MicrocmsImage from '@atoms/MicrocmsImage'
import ExpandingImageLink from '@molecules/ExpandingImageLink'
import SaveButton from '@molecules/SaveButton'
import VoicePlayButton from '@molecules/VoicePlayButton'
import MaskedIcon from '@icons/Masked'
import ArticleRequest from '@src/components/ArticleRequest'
import { StoreContext } from '@models/Store'
import { useTranslation } from '@models/Translation'
import { Article, useAllArticles } from '@models/Article'
import { removeHtmlTags } from '@models/Util'

import * as style from './style.module.sass'

import artisanImg from './assets/artisan.jpg'

type ArticleSideBarProps = {
  rankingArticleIds: string[]
}
const ArticleSideBar = ({ rankingArticleIds }: ArticleSideBarProps) => {
  const { locale, t } = useTranslation()
  const allArticles = useAllArticles()
  const rankingArticles = rankingArticleIds
    .map(id => allArticles.find(article => article.blogId === id))
    .filter(article => article !== undefined)
    .slice(0, 5) as Article[] // filerでundefinedを消しているため強制

  return (
    <div className={style.side}>
      <div className={style.content}>
        <div className={style.label}>CONCEPT VIDEO</div>
        <iframe
          src="https://player.vimeo.com/video/1067952527?h=75f2ccb6ca&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479"
          allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
          title="fashion tech news Concept Video"
          loading="lazy"
        />
        <Link to="/about">
          <div className={style.description}>
            {removeHtmlTags(t('video.top'))}
          </div>
        </Link>
      </div>

      <div className={style.content}>
        <div className={style.label}>PICKUP CONTENTS</div>
        <Link to="/artisan">
          <div className={style.image}>
            <img src={artisanImg} alt="Artisan" />
          </div>
          <div className={style.description}>{t('artisan.articleSidebar')}</div>
        </Link>
      </div>

      <div className={style.content}>
        <div className={style.label}>
          {locale === 'ja' ? (
            <strong>人気記事ランキング</strong>
          ) : (
            'TOP ARTICLES'
          )}
        </div>
        {rankingArticles.map((article, index) => (
          <div className={style.articleContainer} key={article.blogId}>
            <ArticleItem article={article} order={index + 1} />
          </div>
        ))}
      </div>

      {locale === 'ja' && (
        <div className={style.content}>
          <ArticleRequest />
        </div>
      )}
    </div>
  )
}

type ArticleItemProps = {
  article: Article
  order: number
}
const ArticleItem = ({ article, order }: ArticleItemProps) => {
  const { entity } = useContext(StoreContext)
  const { navigate } = useTranslation()
  const isMasked = entity?.user?.isSignIn !== true && article.isMembersOnly

  return (
    <ExpandingImageLink to={article.path}>
      <div
        className={style.article}
        data-gtm-click="article-list-side-ranking"
        data-gtm-value={article.blogId}
      >
        <div className={style.imageContainer}>
          <MicrocmsImage
            image={article.thumbnail}
            alt={article.title}
            type="thumbnail"
            disableAspectRatio
          />
          <div className={style.order} data-order={order}>
            {order}
          </div>
        </div>
        <div className={style.textContainer}>
          <p className={style.title}>
            {article.title}
            {isMasked && (
              <span className={style.masked}>
                <MaskedIcon />
              </span>
            )}
          </p>
          <div className={style.details}>
            <div
              className={style.category}
              onClick={event => {
                event.preventDefault()
                navigate(`/${article.data.category}`)
              }}
            >
              {article.data.category.toUpperCase()}
            </div>
            <time className={style.date}>{article.publishDate}</time>

            {article.enableVoice && (
              <div className={style.voiceContainer}>
                <VoicePlayButton article={article} />
              </div>
            )}

            <div
              className={style.saveContainer}
              data-gtm-click={'save-list-side-ranking'}
              data-gtm-label={entity.bookmark[article.blogId] !== true}
              data-gtm-value={article.blogId}
            >
              <SaveButton
                blogId={article.blogId}
                active={entity.bookmark[article.blogId] === true}
              />
            </div>
          </div>
        </div>
      </div>
    </ExpandingImageLink>
  )
}

export default ArticleSideBar
